import { Schemas } from 'src/openapi/generated/types';
import { sleep } from './sleep';
import { ReservationFlowInvalidRequestError } from 'src/interfaces/reservation_flow';
import { isAPIError, isAxiosError } from 'src/openapi/axiosClient';
import { isReservationFlowInvalidRequestError } from 'src/state-machines/booking/functions/reservation-flow-invalid-request/is-reservation-flow-invalid-request-error';
import { isStripeSpecificError } from './payment';

// 409,429エラーを判定する
const isApiErrorWithTooManyRequestsOrConflict = (
  error: unknown,
): error is { response: { data: Schemas.APIError } } => {
  return !!(
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'data' in error.response &&
    error.response.data &&
    typeof error.response.data === 'object' &&
    'code' in error.response.data &&
    (error.response.data.code === 'too_many_requests' ||
      error.response.data.code === 'conflict')
  );
};

/**
 * 429: TooManyRequestsError
 * 409: ConflictRequestError
 * 上記の場合のみ一定回数リトライ処理するための関数
 * @param apiCall 実行するAPI呼び出し関数
 */
export const executeWithRetryForSpecificError = async <T>(
  apiCall: () => Promise<T>,
): Promise<T> => {
  const maxRetries = 3;
  const retryDelayBase = 3000; // 3秒
  let attempt = 0;
  let lastError: unknown;
  while (attempt < maxRetries) {
    try {
      await sleep(retryDelayBase * attempt);
      return await apiCall();
    } catch (error: unknown) {
      if (!isApiErrorWithTooManyRequestsOrConflict(error)) {
        // 409,429ではない場合初回からerrorにする
        throw error;
      }
      lastError = error;
      attempt += 1;
    }
  }
  throw lastError;
};

// 予約時や回数券・月謝の事前決済で復旧できないエラーが発生した場合に利用する固定エラー
export const fallbackUnrecoverableError: ReservationFlowInvalidRequestError = {
  code: 'internal_server_error',
  message: 'internal_server_error',
  details: [
    {
      type: 'reservation_flow_invalid_request',
      invalid_items: [
        {
          type: 'unrecoverable_error',
          message: 'FALLBACK_UNRECOVERABLE_ERROR',
        },
      ],
    },
  ],
  error: {
    type: 'internal_server_error',
  },
};

// NOTE: Stripe決済固有の処理が含まれているが、エラーハンドリングの順番が重要なため許容しています
// 元々のStripe決済用に実装されている共通のエラー処理をMulpay決済導入時に共通化させました。
// 1. isAxiosError → 2. isStripeSpecificError → 3. その他のエラー処理
// この順序でないと正常に動作しない可能性があります
export const handleHoldAndConfirmationApiError = (error: unknown): never => {
  if (isAxiosError(error) && typeof error.response !== 'undefined') {
    if (
      isAPIError(error.response.data) &&
      isReservationFlowInvalidRequestError(error.response.data)
    ) {
      throw error.response.data;
    }
  }

  if (isStripeSpecificError(error)) {
    throw error;
  }

  // unrecoverable_errorとしてハンドリングする
  throw fallbackUnrecoverableError;
};
